import React, { useState } from 'react';
import {
  Accordion, SegmentedControl, Select, Stack, Text,
} from '@mantine/core';
import {
  DEFAULT_BUTTON_STYLE,
  HOVER_COLOR_NONE,
} from '../../../shared/boxes/button/buttonStyles';
import { PuckColorFieldWithPalette } from './createPuckColorField';
import { PxSpacingInput } from './puckPxSpacingField';
import InspectorRow from './InspectorRow';
import { SHADOW_OPTIONS } from '../../../shared/boxes/shadowStyles';

/** Angles proposés — un dégradé ne demande pas un rapporteur. */
const GRADIENT_ANGLES = [
  { value: '90', label: '→' },
  { value: '135', label: '↘' },
  { value: '180', label: '↓' },
  { value: '225', label: '↙' },
];

const ANIMATIONS = [
  { value: 'none', label: 'Aucune' },
  { value: 'press', label: 'Enfoncement' },
  { value: 'ripple', label: 'Ondulation' },
  { value: 'lift', label: 'Relèvement' },
];

/**
 * Trois cas, trois entrées nommées. Le nuancier seul ne pouvait pas les
 * exprimer : vider le champ voulait déjà dire « déduis-la », il n'y avait donc
 * aucun moyen de demander un bouton qui ne bouge pas au survol.
 */
const HOVER_MODES = [
  { value: 'auto', label: 'Automatique' },
  { value: 'none', label: 'Aucun changement' },
  { value: 'custom', label: 'Couleur choisie' },
];

const ICON_SIDES = [
  { value: 'left', label: 'À gauche' },
  { value: 'right', label: 'À droite' },
  { value: 'only', label: 'Seule' },
];

/**
 * Réglages fins d'un bouton : le survol se déduit de la couleur, le focus n'est
 * pas négociable et le clic a son animation par défaut. Personne n'a besoin
 * d'y toucher pour obtenir un bouton correct — d'où leur place dans la section
 * « Réglages avancés », repliée par défaut.
 *
 * Écrit la prop objet `buttonStyle` entière — l'arrondi et les traits, eux,
 * restent dans `border` (puckBorderField), partagé avec les autres box.
 */
export function ButtonStyleSettings({ value, onChange }) {
  const style = { ...DEFAULT_BUTTON_STYLE, ...(value || {}) };
  const patch = (partial) => onChange({ ...style, ...partial });
  const isGradient = style.variant === 'gradient';
  /* « Couleur choisie » sans couleur encore choisie n'est pas un état
     stockable : on n'écrit pas une couleur arbitraire en base pour faire tenir
     un select. Le mode vit donc en local le temps que l'utilisateur en pique
     une, et retombe sur « Automatique » s'il n'en pique aucune. */
  const [pendingCustom, setPendingCustom] = useState(false);
  const storedHoverMode = resolveHoverMode(style);
  const hoverMode = pendingCustom && storedHoverMode === 'auto' ? 'custom' : storedHoverMode;

  return (
    <Stack gap={12}>
      {isGradient && (
        <>
          <InspectorRow label="Dégradé — départ">
            <PuckColorFieldWithPalette
              label={null}
              value={style.gradientFrom}
              onChange={(gradientFrom) => patch({ gradientFrom })}
            />
          </InspectorRow>
          <InspectorRow label="Dégradé — arrivée">
            <PuckColorFieldWithPalette
              label={null}
              value={style.gradientTo}
              onChange={(gradientTo) => patch({ gradientTo })}
            />
          </InspectorRow>
          <InspectorRow label="Sens du dégradé" layout="stacked">
            <SegmentedControl
              fullWidth
              size="xs"
              color="editor"
              data={GRADIENT_ANGLES}
              value={String(style.gradientAngle)}
              onChange={(v) => patch({ gradientAngle: Number(v) })}
            />
          </InspectorRow>
        </>
      )}

      <Select
        size="xs"
        label="Ombre portée"
        data={SHADOW_OPTIONS}
        value={style.shadow}
        onChange={(shadow) => shadow && patch({ shadow })}
        allowDeselect={false}
        comboboxProps={{ withinPortal: false }}
      />

      <Select
        size="xs"
        label="Animation au clic"
        description="Neutralisée si le visiteur a réduit les animations."
        data={ANIMATIONS}
        value={style.pressAnimation}
        onChange={(pressAnimation) => pressAnimation && patch({ pressAnimation })}
        allowDeselect={false}
        comboboxProps={{ withinPortal: false }}
      />

      <Select
        size="xs"
        label="Position de l'icône"
        data={ICON_SIDES}
        value={style.iconSide}
        onChange={(iconSide) => iconSide && patch({ iconSide })}
        allowDeselect={false}
        comboboxProps={{ withinPortal: false }}
      />

      <PxSpacingInput
        value={style.paddingX}
        onChange={(paddingX) => patch({ paddingX })}
        label="Espace latéral"
        placeholder="Auto"
        max={120}
      />
      <PxSpacingInput
        value={style.paddingY}
        onChange={(paddingY) => patch({ paddingY })}
        label="Espace vertical"
        placeholder="Auto"
        max={80}
      />

      <Stack gap={12}>
        <Select
          size="xs"
          label="Couleur au survol"
          description={HOVER_HINTS[hoverMode]}
          data={HOVER_MODES}
          value={hoverMode}
          onChange={(mode) => {
            if (!mode) return;
            setPendingCustom(mode === 'custom');
            if (mode !== hoverMode) patch({ hoverColor: hoverValue(mode, style) });
          }}
          allowDeselect={false}
          comboboxProps={{ withinPortal: false }}
        />
        {hoverMode === 'custom' && (
          <InspectorRow label="Couleur">
            <PuckColorFieldWithPalette
              label={null}
              value={style.hoverColor}
              onChange={(hoverColor) => patch({ hoverColor: hoverColor || '' })}
            />
          </InspectorRow>
        )}
      </Stack>
    </Stack>
  );
}

/**
 * Les mêmes réglages repliés dans un accordéon, pour le bouton d'une slide :
 * dans un élément de liste, il n'y a pas de section où les ranger.
 */
export default function ButtonStyleField({ value, onChange }) {
  return (
    <Accordion
      variant="contained"
      chevronPosition="right"
      styles={{ content: { padding: 8 } }}
    >
      <Accordion.Item value="advanced">
        <Accordion.Control>
          <Text size="xs" fw={500}>Réglages avancés</Text>
        </Accordion.Control>
        <Accordion.Panel>
          <ButtonStyleSettings value={value} onChange={onChange} />
        </Accordion.Panel>
      </Accordion.Item>
    </Accordion>
  );
}

const HOVER_HINTS = {
  auto: 'Déduite de la couleur du bouton : assombrie si elle est claire, éclaircie si elle est sombre.',
  none: 'Le fond du bouton ne change pas au survol.',
  custom: null,
};

/** Le mode que décrit la valeur stockée. */
function resolveHoverMode(style) {
  if (style.hoverColor === HOVER_COLOR_NONE) return 'none';
  return style.hoverColor ? 'custom' : 'auto';
}

/** La valeur qu'écrit un changement de mode. */
function hoverValue(mode, style) {
  if (mode === 'none') return HOVER_COLOR_NONE;
  // « Automatique » comme « Couleur choisie » repartent de vide : le nuancier
  // qui s'affiche sous « Couleur choisie » écrira la couleur.
  return mode === 'auto' || style.hoverColor === HOVER_COLOR_NONE ? '' : style.hoverColor;
}

export function puckButtonStyleSettingsField(label = 'Apparence') {
  return {
    type: 'custom',
    label,
    render: (props) => <ButtonStyleSettings {...props} />,
  };
}
